"use client";

import React from "react";
import { useMediaQuery } from "modules/common/hooks/useMediaQuery";
import { Package, SearchIcon } from "lucide-react";
import { cn } from "utils/utils";
import { CardPreviewSizing } from "utils/styles";
import { CardConditionEnum } from "modules/cards/model/card";
import { TradeItem, TradeListType } from "modules/tradeLists/model/tradeList";
import { CityItemStore } from "modules/city/model/cityCards";
import { SizingOptions } from "modules/common/components/CardPreviewLoading";
import { RelativeTime } from "modules/common/components/ui/RelativeTime";
import { UserHoverCard } from "modules/common/components/navigation/UserHoverCard";
import { StoreDropdownButton } from "modules/city/components/client/StoreDropdownButton";
import { CardGridItem } from "modules/common/components/CardGridItem";
import {
  getSelectionState,
  useMultiSelectStore,
} from "modules/common/stores/useMultiSelectStore";
import { SelectionMode } from "modules/common/components/CardGridItem/CardGridItemRoot";
import { CardPrimaryPrice } from "modules/cards/components/CardPrimaryPrice";
import {
  CardFinishBadge,
  CardSpecChip,
} from "modules/common/components/CardSpecOverlay";
import { primaryDisplayFinish } from "modules/cards/model/finishBehavior";
import { ActiveProposalBadge } from "modules/tradeLists/components/ActiveProposalBadge";
import type { ImageProps } from "next/image";

type RingVariant = "primary" | "secondary" | "warning";

interface TradeItemGridCellProps {
  item: TradeItem;
  sizing?: SizingOptions;
  priority?: boolean;
  loading?: ImageProps["loading"];
  /** Matched-trades context: show direction ribbon, force "message" own-item variant. */
  fromMatch?: boolean;
  /** Hide the trader avatar + name (user-profile / own-trade-list contexts). */
  hideUser?: boolean;
  /** Local store presence (city contexts). Hidden when empty or undefined. */
  stores?: CityItemStore[];
  /** Optional page-specific context marker rendered in the footer. */
  contextBadge?: React.ReactNode;
  /** Enables the multi-select checkbox overlay. */
  enableMultiSelect?: boolean;
  /** Keeps the checkbox mounted but visually absent until its caller enables it. */
  selectionCheckboxVisible?: boolean;
  /** Username truncation in the footer (defaults match the prior CityCardItem behaviour). */
  usernameMaxLength?: number;
  /**
   * Optional absolutely-positioned overlay rendered inside the relative card
   * art stage (e.g. the owner's "more actions" menu or a Binder list ribbon).
   * Children get the same CardGridItemContext as the cell.
   */
  actionsOverlay?: React.ReactNode;
  /**
   * Optional override for the card image click handler (defaults to opening
   * the card details modal). Used on owner pages to open the edit modal
   * instead.
   */
  onImageClick?: () => void;
  /**
   * Owner-only active-trade navigation. When omitted, the availability badge
   * remains the existing noninteractive status indicator.
   */
  onOpenActiveTrade?: () => void;
  /**
   * Suppress the bottom action button entirely (e.g. owner-on-own-list,
   * where the "Edit listing" link points to the same page they're on).
   */
  hideAction?: boolean;
  /**
   * Hide the relative timestamp ("3h ago"). Useful on owner-on-own-list,
   * where there is no inter-user context that benefits from the freshness
   * signal.
   */
  hideTime?: boolean;
  /**
   * Explicit selection mode passthrough. When provided, overrides the
   * mode derived from `enableMultiSelect` — used by the proposal picker
   * to pass controlled selection (selected / onToggle / isAnySelected).
   */
  selectionMode?: SelectionMode;
  /** Ring color when the cell is selected. Defaults to "primary". */
  ringVariant?: RingVariant;
  /** Prevent selection while keeping the card visible as unavailable inventory. */
  disabled?: boolean;
  disabledLabel?: string;
  /**
   * Softer variant of `disabled` for browse surfaces: outside a multi-select
   * this is ordinary browsable inventory whose card details open on click.
   * Once a multi-select is in progress the cell mutes, its checkbox renders
   * inert, and the card image goes inert with it — every neighbour toggles on
   * click, so letting this one open details instead reads as a misfire. The
   * trade-off is deliberate: card details for spoken-for inventory are
   * reachable before a selection starts and after it is cleared, not during.
   * A cell already in the selection is exempt (see `blockSelection`).
   */
  selectionDisabled?: boolean;
  /**
   * Why `selectionDisabled` is set, for the inert checkbox's aria-label and
   * the muted image's. Defaults to the trade-surface reason; the Binder also
   * blocks selection while copy ids are still syncing, where that sentence
   * would be plainly false.
   */
  selectionDisabledLabel?: string;
}

interface DirectionLook {
  Icon: typeof Package;
  label: string;
  badge: string;
}

// "Want" → secondary, "Have" → warning, matching tradeRelationshipUtils so
// the ribbon agrees with the relationship color used elsewhere in the app.
function directionLook(item: TradeItem): DirectionLook {
  const isWants = item.list?.type === TradeListType.WANTS;
  return isWants
    ? { Icon: SearchIcon, label: "They want", badge: "badge-secondary" }
    : { Icon: Package, label: "They have", badge: "badge-warning" };
}

// Bottom-centre spec chip. Outer container reuses the set-cards-page
// price-chip recipe (`badge badge-soft` + `border-base-content/10` +
// `bottom-4.5`) so the typography/anchor stays consistent across the app.
// Inside, the chip is segmented: condition FILLS its left segment with the
// condition color (true to the design — not a chip-inside-a-chip), then a
// 1px divider, then price, divider, then language flag.
function SpecChip({ item }: { item: TradeItem }) {
  const showMinimum =
    item.list?.type === TradeListType.WANTS &&
    item.condition !== CardConditionEnum.Mint;

  return (
    <CardSpecChip
      condition={item.condition}
      language={item.language}
      quantity={item.quantity}
      showMinimumCondition={showMinimum}
      value={
        item.anyPrinting ? (
          <span
            className="tooltip tooltip-secondary inline-flex items-center"
            data-tip="Any set / printing"
          >
            Any set
          </span>
        ) : (
          <CardPrimaryPrice
            card={item}
            emptyFallback={<span className="text-base-content/60">—</span>}
          />
        )
      }
    />
  );
}

// Direction ribbon, anchored just below the card's title bar (so the
// card name stays legible). Reuses DaisyUI badge colors instead of
// bespoke classes — same secondary/warning pair as the matched-trade
// tabs. Default `badge` size (one step up from `badge-sm`) gives it
// just enough presence to register as the matched-trade signal
// without dominating the card art.
function DirectionRibbon({ item }: { item: TradeItem }) {
  // Orphaned/card-deleted rows have no list; suppress the ribbon rather
  // than mislabelling them as "They have" (directionLook's null fallback).
  if (!item.list) return null;
  const { Icon, label, badge } = directionLook(item);
  return (
    <span
      className={cn(
        "badge absolute left-0 top-[12%] z-[4] gap-1",
        "rounded-l-none font-semibold shadow-md",
        badge
      )}
    >
      <Icon size={13} />
      {label}
    </span>
  );
}

export function TradeItemGridCell({
  item,
  sizing = CardPreviewSizing,
  priority,
  loading,
  fromMatch = false,
  hideUser = false,
  stores,
  contextBadge,
  enableMultiSelect = false,
  selectionCheckboxVisible = true,
  usernameMaxLength,
  actionsOverlay,
  onImageClick,
  onOpenActiveTrade,
  hideAction = false,
  hideTime = false,
  selectionMode,
  ringVariant = "primary",
  disabled = false,
  disabledLabel,
  selectionDisabled = false,
  selectionDisabledLabel = "All copies are already in an active trade",
}: TradeItemGridCellProps) {
  const isDesktop = useMediaQuery("(min-width: 768px)");

  const displayFinish = primaryDisplayFinish(item.finishes);

  const showStores = !!stores && stores.length > 0;
  // Hiding the user implicitly hides the timestamp — without the trader
  // attribution there's no inter-user context that benefits from the
  // freshness signal, so "X hours ago" floating alone reads as noise.
  const showTime = !hideTime && !hideUser && !!item.updatedAt;
  const showFooter =
    (!hideUser && !!item.user) || showTime || showStores || !!contextBadge;
  const showPanel = showFooter || !hideAction;

  const mobileMax = 7;
  const desktopMax = 20;
  const nameMax = usernameMaxLength ?? (isDesktop ? desktopMax : mobileMax);

  // The mute is scoped to an in-progress multi-select. Outside it this is
  // ordinary browsable inventory whose details still open on click, and a
  // permanent grey would read as "dead" on a card that isn't. Same trigger the
  // selection checkboxes use (SelectionVisibilityWrapper), so the whole
  // multi-select affordance appears and disappears together.
  const isSelecting = useMultiSelectStore(
    (s) => s.selectAllActive || s.selectedIds.size > 0
  );
  const selectAllActive = useMultiSelectStore((s) => s.selectAllActive);
  // `selectionDisabled` blocks *new* selections only. The availability
  // overlay refreshes in the background, so a card that was ticked while it
  // had free copies can go fully spoken-for while it sits in the selection —
  // muting that one would strand it: unchecked-looking, inert, still counted
  // in `selectedIds`, and only escapable via "clear selection".
  const isSelected = useMultiSelectStore(
    (s) => getSelectionState(s.selectedIds, item.ids) !== "none"
  );
  const blockSelection = selectionDisabled && !isSelected;
  const showAsUnselectable = blockSelection && isSelecting;

  // Explicit selectionMode wins; otherwise derive from enableMultiSelect.
  const effectiveSelectionMode: SelectionMode =
    disabled || blockSelection
      ? { type: "none" }
      : (selectionMode ??
        (enableMultiSelect ? { type: "store" } : { type: "none" }));
  const selectionEnabled = effectiveSelectionMode.type !== "none";
  // A card whose availability blocks new selections still gets the corner
  // checkbox, just inert — hiding it entirely would read as a rendering
  // fault next to every selectable neighbour, and with checkboxes now always
  // visible that hole is permanent rather than hover-gated. `disabled` cells
  // (proposal-picker cards already locked into an active trade) get the same
  // treatment for the same reason. But only where selection is offered at
  // all: callers pass `selectionDisabled` unconditionally while gating
  // `enableMultiSelect` on viewer identity, so `blockSelection` on its own
  // must not summon a checkbox into an owner's or a signed-out grid that has
  // no selection feature.
  const selectionOffered = !!selectionMode || enableMultiSelect;
  // `isSelected` is the backstop: a caller that forces `selectionMode:"none"`
  // on a cell whose ids are still in the store would otherwise render no
  // checkbox and no ring while the bulk action still submits it. Whatever the
  // caller decided, a card in the selection has to show that it is.
  const showCheckbox =
    selectionOffered &&
    (selectionEnabled || blockSelection || disabled || isSelected);
  // Mid-selection the card art is inert too, not just the checkbox. Every other
  // card in the grid toggles on click; letting this one open card details
  // instead makes the odd one out feel like the click misfired. Outside a
  // selection it stays ordinary browsable inventory whose details do open.
  const imageDisabled = disabled || showAsUnselectable;
  // `checked` is only read once the checkbox is inert, so it has to restate
  // what the live control would have shown. A controlled caller owns that
  // answer (the store's ids say nothing about a picker's selection); a
  // store-driven cell is checked under select-all even though the store holds
  // no ids, same as CardGridItemRoot reports "all".
  const checkedWhenInert =
    selectionMode?.type === "controlled"
      ? selectionMode.selected
      : isSelected || selectAllActive;

  return (
    <CardGridItem.Root
      item={item}
      sizing={sizing}
      selectionMode={effectiveSelectionMode}
      ringVariant={ringVariant}
      hasPanel={showPanel}
      // h-full lets the cell stretch to fill its grid row (the parent
      // grid uses items-stretch), so cells with shorter content don't
      // leave a gap at the bottom of the row.
      className={cn(
        "flex h-full flex-col",
        imageDisabled && "cursor-not-allowed",
        imageDisabled && "opacity-60 grayscale-[0.25]"
      )}
      style={{ width: sizing.width, minWidth: sizing.minWidth }}
    >
      {/* Image stage — keeps the card image clickable and stacks overlays
          (ribbon / foil / spec chip) on top. */}
      <div className="relative w-full">
        <CardGridItem.Image
          priority={priority}
          loading={loading}
          onClick={onImageClick}
          disabled={imageDisabled}
          disabledLabel={disabled ? disabledLabel : selectionDisabledLabel}
        />
        {showCheckbox && (
          <CardGridItem.SelectionOverlay
            variant={ringVariant}
            visible={selectionCheckboxVisible}
            checked={checkedWhenInert}
            disabled={disabled || blockSelection}
            disabledLabel={disabled ? disabledLabel : selectionDisabledLabel}
          />
        )}
        {fromMatch && <DirectionRibbon item={item} />}
        <ActiveProposalBadge
          item={item}
          onOpenTrade={onOpenActiveTrade}
          activeTradeCount={item.activeTrades?.length}
          className={cn(
            "absolute left-0 z-[7] flex",
            fromMatch ? "top-[calc(12%+2rem)]" : "top-[12%]"
          )}
          badgeClassName="rounded-l-none shadow-md"
        />
        {displayFinish && (
          <CardFinishBadge
            finish={displayFinish}
            className="badge-sm font-semibold"
          />
        )}
        <SpecChip item={item} />
        {actionsOverlay}
      </div>

      {/* Panel below the card — minimal footer (trader / time / stores)
          plus the action button. flex-1 stretches the panel to fill any
          remaining row height; mt-auto on the action area anchors it to
          the bottom so all cells line up regardless of footer content.
          When everything inside is suppressed (e.g. owner-on-own-list with
          hideUser + hideTime + hideAction) the panel collapses entirely so
          the bordered drawer doesn't render empty under the card. */}
      {showPanel && (
        <div className="rounded-b-box border-base-content/20 bg-base-100 -mt-4 flex w-full flex-1 flex-col border p-2 pt-2">
          <h3 className="sr-only">{item.name}</h3>
          <div className="flex flex-1 flex-col gap-3 pt-3 lg:pt-4">
            {showFooter && (
              <div className="flex flex-col gap-2">
                {contextBadge && <div className="flex">{contextBadge}</div>}
                <div className="flex items-center justify-between gap-1">
                  <div className="flex min-w-0 items-center gap-2">
                    {!hideUser && item.user && (
                      <UserHoverCard
                        userId={item.user.id}
                        size="xs"
                        username={item.user.username}
                        avatar={item.user.preferredAvatar}
                        maxUsernameLength={nameMax}
                        usernameClassName="text-base-content text-xs font-semibold"
                        activityStatus={item.activityStatus}
                      />
                    )}
                    <span className="text-base-content/70 justify-end truncate text-xs">
                      {showTime && <RelativeTime date={item.updatedAt} />}
                    </span>
                  </div>

                  {showStores && (
                    <StoreDropdownButton
                      stores={stores}
                      item={item}
                      size="xs"
                    />
                  )}
                </div>
              </div>
            )}

            {!hideAction && (
              <div className="mt-auto">
                <CardGridItem.ContactActions
                  fromMatch={fromMatch}
                  ownItemVariant={fromMatch ? "message" : "link"}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </CardGridItem.Root>
  );
}

export default TradeItemGridCell;
